import { ReleaseNoteGitHubCommit } from "@/service/response/admin-release-notes";

export const RELEASE_NOTE_PROMPT = `Você vai escrever uma nota de atualização da plataforma Poupenergia. Quem lê são os usuários da plataforma: gestores de energia e clientes, pessoas sem conhecimento técnico. A nota deve contar o que mudou na plataforma para eles e como isso melhora o dia a dia de quem a usa.

## Fonte das informações

No fim desta mensagem há um JSON com a lista de merges de promoção (dev → main, main → staging e staging → production) que entram nesta nota. Cada item traz o repositório no GitHub ("repository"), o SHA completo do merge commit ("sha"), o número do pull request de promoção ("pullRequest"), as branches de origem e destino ("sourceBranch" e "targetBranch") e a data do merge ("mergedAt").

A nota deve sair da sua própria análise do código, e você tem acesso ao repositório completo. Antes de escrever:

1. Para cada item, obtenha a diff que a promoção levou: a diferença entre o primeiro e o segundo pai do merge commit (o equivalente a "git diff <sha>^1...<sha>^2").
2. Analise essa diff junto com o restante do repositório para entender o efeito real de cada mudança para o usuário: quais telas, textos, fluxos, regras de negócio, cálculos e dados mudaram. Navegue pelo código ao redor (componentes, rotas, serviços, regras) sempre que a diff sozinha não deixar claro o que a pessoa passa a ver ou a conseguir fazer.
3. Junte o que veio de todos os repositórios. O backend afeta tudo o que os usuários veem; "web_manager" é o painel do gestor e "web_client" é o portal do cliente. Mudanças só no painel administrativo interno ("web_admin") não são vistas pelos usuários e ficam de fora.
4. A mesma mudança pode aparecer em mais de uma promoção (dev → main e depois main → staging, por exemplo). Conte-a uma vez só.
5. Descarte o que não tem efeito percebido pelo usuário: refatoração, testes, CI, dependências, logs, ajustes internos de infraestrutura e performance imperceptível.
6. Agrupe as mudanças restantes por módulo ou funcionalidade da plataforma (por exemplo: Faturas, Economia, Usinas, Unidades consumidoras, Relatórios, Base de conhecimento). Mudanças pequenas sobre o mesmo assunto viram uma seção só.

Não invente nada que não esteja nas diffs dessas promoções. Se o efeito de uma mudança não ficar claro mesmo depois de analisar o repositório, descreva só o que dá para afirmar com segurança.

## Como escrever

- Português do Brasil, tom claro, próximo e positivo, sem exageros de marketing.
- Fale do ponto de vista de quem usa a plataforma: o que a pessoa passa a conseguir fazer, o que ficou mais fácil, mais rápido ou mais confiável.
- Use o nome das telas e funcionalidades como o usuário as vê, nunca o nome técnico.
- Para correções, explique o que passa a funcionar corretamente, sem dramatizar o erro anterior.
- Não cite SHA, número de pull request, código de ticket (AB#...), nome de branch, repositório, arquivo, endpoint, tabela, biblioteca nem qualquer outro detalhe técnico.

## Estrutura da nota

# Título curto que resuma a atualização

Introdução de 2 a 3 frases sobre o conjunto das mudanças e o que elas trazem para quem usa a plataforma.

## Nome do módulo ou funcionalidade

Um parágrafo curto explicando o que foi feito (novidade, melhoria ou correção) e como a plataforma ficou melhor com isso.

- Destaque opcional, quando a seção tiver mais de um ponto relevante

(repita uma seção "##" para cada módulo ou funcionalidade; comece pelas novidades mais relevantes e deixe as correções por último)

## Formato (editor de markdown do painel)

A nota será colada num editor de markdown com recursos limitados. Siga estas regras para que ela apareça corretamente:

- Responda somente com o markdown da nota, sem comentários antes ou depois e sem bloco de código em volta.
- Use "#" uma única vez, na primeira linha, para o título. Para as seções use "##" ou "###" para níveis menores.
- Separe cada parágrafo, título, lista, citação e tabela com uma linha em branco.
- Escreva cada parágrafo numa linha só, sem quebras de linha no meio.
- Listas só com "-", um item por linha e sem listas aninhadas. Não use lista numerada.
- Pode usar **negrito** e *itálico* para destacar termos. Use citação com ">" apenas para um aviso importante e tabela apenas quando a mudança pedir uma comparação.
- Não use HTML, imagem, vídeo, bloco de código, código em linha, linha horizontal nem notas de rodapé.
- Use emojis em títulos.
- Escreva o markdown para eu copiar e colar no meu editor de texto.`;

export const buildReleaseNotePrompt = (mergeCommits: ReleaseNoteGitHubCommit[]) => {
  const payload = {
    promotionMerges: mergeCommits.map(({ repository, sha, pullRequest, sourceBranch, targetBranch, mergedAt }) => ({
      repository,
      sha,
      pullRequest,
      sourceBranch,
      targetBranch,
      mergedAt,
    })),
  };

  return `${RELEASE_NOTE_PROMPT}\n\n## Merges de promoção\n\n${JSON.stringify(payload, null, 2)}`;
};
